import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  FormHelperText,
  Radio,
  RadioGroup,
  FormLabel,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { useHistory } from 'react-router-dom';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  helpText: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
});

const EVENT_TYPES = [
  {
    value: 'readiness_completed',
    label: 'Readiness Completed',
    description: 'Fires when a user completes their succession readiness assessment',
  },
  {
    value: 'partial_sale_published',
    label: 'Partial Sale Published',
    description: 'Fires when a user publishes a partial sale listing',
  },
  {
    value: 'login',
    label: 'User Login',
    description: 'Fires every time a user logs in',
  },
  {
    value: 'inactivity_7days',
    label: '7-Day Inactivity',
    description: 'Fires when a user has been inactive for 7 days',
  },
  {
    value: 'inactivity_30days',
    label: '30-Day Inactivity',
    description: 'Fires when a user has been inactive for 30 days',
  },
  {
    value: 'vix_tier_change',
    label: 'VIX Tier Change',
    description: 'Fires when a user\'s VIX tier changes',
  },
];

const TriggerBuilder = ({
  classes,
  trigger,
  templates,
  sequences,
  onSave,
  onCancel,
}) => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [eventType, setEventType] = useState('');
  const [actionType, setActionType] = useState('send_email');
  const [templateId, setTemplateId] = useState('');
  const [sequenceId, setSequenceId] = useState('');
  const [active, setActive] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [conditionJson, setConditionJson] = useState('{}');

  useEffect(() => {
    if (trigger) {
      setName(trigger.name || '');
      setEventType(trigger.eventType || '');
      setActionType(trigger.actionType || 'send_email');
      setTemplateId(trigger.emailTemplate?.id || '');
      setSequenceId(trigger.emailSequence?.id || '');
      setActive(trigger.active !== undefined ? trigger.active : true);
      setRequiresApproval(trigger.requiresApproval || false);
      setConditionJson(trigger.conditionJson || '{}');
    }
  }, [trigger]);

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a trigger name');
      return;
    }

    if (!eventType) {
      alert('Please select an event type');
      return;
    }

    if (actionType === 'send_email' && !templateId) {
      alert('Please select an email template');
      return;
    }

    if (actionType === 'add_to_sequence' && !sequenceId) {
      alert('Please select a sequence');
      return;
    }

    // Validate JSON
    try {
      JSON.parse(conditionJson);
    } catch (e) {
      alert('Invalid JSON in conditions field. Please fix the syntax.');
      return;
    }

    const triggerData = {
      name: name.trim(),
      eventType,
      actionType,
      active,
      requiresApproval,
      conditionJson,
    };

    if (actionType === 'send_email') {
      triggerData.templateId = templateId;
    } else {
      triggerData.sequenceId = sequenceId;
    }

    if (trigger?.id) {
      triggerData.id = trigger.id;
    }

    if (onSave) {
      onSave(triggerData);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.push('/admin/email_triggers');
    }
  };

  const selectedEvent = EVENT_TYPES.find(e => e.value === eventType);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">
          {trigger?.id ? 'Edit Trigger' : 'Create New Trigger'}
        </Typography>
      </div>

      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Trigger Details
        </Typography>

        <TextField
          label="Trigger Name"
          variant="outlined"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={classes.formField}
          helperText="A descriptive name for this automation trigger"
        />

        <FormControl variant="outlined" fullWidth required className={classes.formField}>
          <InputLabel>Event Type</InputLabel>
          <Select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            label="Event Type"
          >
            <MenuItem value="">
              <em>Select an event</em>
            </MenuItem>
            {EVENT_TYPES.map(event => (
              <MenuItem key={event.value} value={event.value}>
                {event.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>When should this trigger fire?</FormHelperText>
        </FormControl>

        {selectedEvent && (
          <Box className={classes.helpText}>
            <Typography variant="body2">
              <strong>{selectedEvent.label}:</strong> {selectedEvent.description}
            </Typography>
          </Box>
        )}

        <FormControl component="fieldset" className={classes.formField}>
          <FormLabel component="legend">Action Type</FormLabel>
          <RadioGroup
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            <FormControlLabel
              value="send_email"
              control={<Radio color="primary" />}
              label="Send Email"
            />
            <FormControlLabel
              value="add_to_sequence"
              control={<Radio color="primary" />}
              label="Add to Sequence"
            />
          </RadioGroup>
          <FormHelperText>What should happen when this trigger fires?</FormHelperText>
        </FormControl>

        {actionType === 'send_email' && (
          <FormControl variant="outlined" fullWidth required className={classes.formField}>
            <InputLabel>Email Template</InputLabel>
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              label="Email Template"
            >
              <MenuItem value="">
                <em>Select a template</em>
              </MenuItem>
              {templates.map(template => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} ({template.category})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Which email template should be sent?</FormHelperText>
          </FormControl>
        )}

        {actionType === 'add_to_sequence' && (
          <FormControl variant="outlined" fullWidth required className={classes.formField}>
            <InputLabel>Email Sequence</InputLabel>
            <Select
              value={sequenceId}
              onChange={(e) => setSequenceId(e.target.value)}
              label="Email Sequence"
            >
              <MenuItem value="">
                <em>Select a sequence</em>
              </MenuItem>
              {sequences.filter(s => s.active).map(sequence => (
                <MenuItem key={sequence.id} value={sequence.id}>
                  {sequence.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Which sequence should the user be added to?</FormHelperText>
          </FormControl>
        )}

        <TextField
          label="Conditions (JSON)"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={conditionJson}
          onChange={(e) => setConditionJson(e.target.value)}
          className={classes.formField}
          helperText="Optional: JSON object with conditions for this trigger. Leave as {} to always fire."
        />

        <FormControlLabel
          control={
            <Switch
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              color="primary"
            />
          }
          label="Active (trigger will fire when events occur)"
        />

        {actionType !== 'cancel_sequence' && (
          <FormControlLabel
            control={
              <Switch
                checked={requiresApproval}
                onChange={(e) => setRequiresApproval(e.target.checked)}
                color="primary"
              />
            }
            label="Approved users only (pending and denied users are excluded from this trigger)"
          />
        )}
      </Paper>

      <div className={classes.actionButtons}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          {trigger?.id ? 'Update Trigger' : 'Create Trigger'}
        </Button>
      </div>
    </div>
  );
};

TriggerBuilder.propTypes = {
  classes: PropTypes.object.isRequired,
  trigger: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    eventType: PropTypes.string,
    actionType: PropTypes.string,
    active: PropTypes.bool,
    requiresApproval: PropTypes.bool,
    conditionJson: PropTypes.string,
    emailTemplate: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    emailSequence: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.string,
    })
  ).isRequired,
  sequences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      active: PropTypes.bool,
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

TriggerBuilder.defaultProps = {
  templates: [],
  sequences: [],
};

export default withStyles(styles)(TriggerBuilder);

